"use client"

import { Category } from "@/app/generated/prisma"
import { Routes } from "@/lib/routes"
import { generateSlug, getCategoryTitle } from "@/utils/helpers/utils"
import Link from "next/link"
import { usePathname } from "next/navigation"

type CategoryType = "news" | "topics" | "resources"

type SidebarItem = {
   id: string | number
   name: string
   /** What the user sees. */
   label: string
   isVirtual?: boolean
}

/** Only the identity, the path source and the display label are needed here. */
type SidebarCategory = Pick<Category, "id" | "name" | "title">

type CategorySidebarProps = {
   items: SidebarCategory[]
   title: string
   type: CategoryType
}

function CategorySidebar({ items, title, type }: CategorySidebarProps) {
   const pathname = usePathname()

   const toSidebarItem = (category: SidebarCategory): SidebarItem => ({
      id: category.id,
      name: category.name,
      label: getCategoryTitle(category),
   })

   const normalizedItems: SidebarItem[] = [
      { id: "all", name: "All", label: "All", isVirtual: true },
      ...items.map(toSidebarItem),
   ]

   function getHref(name: string) {
      const isAll = name.toLowerCase() === "all" || name.toLowerCase() === "all categories"
      const slug = generateSlug(name)

      const hrefBuilders: Record<CategoryType, () => string> = {
         resources: () => {
            const pathSegments = pathname.split("/").filter(Boolean)
            const currentResourceType = pathSegments[1] || "library"
            return isAll ? `/resources/${currentResourceType}/` : `/resources/${currentResourceType}/${slug}/`
         },
         news: () => (isAll ? Routes.client.newsPage.getPath() : Routes.client.news.getPath({ slug })),
         topics: () => (isAll ? Routes.client.topics.getPath() : Routes.client.topic.getPath({ slug })),
      }

      return hrefBuilders[type]()
   }

   function isActive(item: SidebarItem) {
      if (type === "resources") {
         const pathSegments = pathname.split("/").filter(Boolean)
         const categorySegment = pathSegments[2]
         if (item.isVirtual) {
            return !categorySegment
         }
         return categorySegment === generateSlug(item.name)
      }

      const cleanPathname = decodeURIComponent(pathname).replace(/\/$/, "").toLowerCase()
      const cleanHref = decodeURIComponent(getHref(item.name)).replace(/\/$/, "").toLowerCase()
      return cleanPathname === cleanHref
   }

   return (
      <div className="w-full">
         <div className="flex flex-col">
            <h2 className="text-custom-3xl-bold text-title">{title}</h2>
            <div className="mb-4 mt-2 h-1 w-1/4 bg-custom-primary" />

            <div className="flex flex-wrap gap-2">
               {normalizedItems.map((category) => {
                  const href = getHref(category.name)
                  const active = isActive(category)

                  return (
                     <Link
                        key={category.id}
                        href={href}
                        aria-label={`View all posts in ${category.label}`}
                        className={`
                           inline-block rounded-md px-3 py-2 font-satoshi text-[14px]/[18px] font-medium capitalize
                           transition-all duration-200
                           ${
                              active
                                 ? "border border-custom-primary/50 bg-custom-primary/10 text-custom-primary"
                                 : "bg-resources-bg text-title"
                           }
                        `}
                     >
                        {category.label}
                     </Link>
                  )
               })}
            </div>
         </div>
      </div>
   )
}

export default CategorySidebar
